import useSWR from 'swr';
import { fetchEndpointData } from '@/utils/fetch.client';

/**
 * `full` grants the minting user's privileges across the REST API. `cli` is far narrower: it
 * reaches the model gateway only, carries its own spend limit, and is what `coding-agent
 * --login` creates. The distinction matters when deciding what to revoke.
 */
export type AgentTokenScope = 'full' | 'cli';

export interface AgentToken {
  id: string;
  tokenPrefix: string;
  teamId: string | null;
  label: string | null;
  createdAt: string;
  expiresAt: string;
  scope: AgentTokenScope;
  /** Throttled server-side to one write per five minutes — "recently used", not exact. */
  lastUsedAt: string | null;
}

export interface CreateAgentTokenResponse extends AgentToken {
  /** Full plaintext secret — shown exactly once, never persisted. */
  token: string;
}

interface ListAgentTokensResponse {
  tokens: AgentToken[];
}

interface RevokeAgentTokenResponse {
  revoked: string;
}

export function useAgentTokens() {
  const { data, mutate, isLoading, error } = useSWR('/api/v1/user/agent-tokens', () =>
    fetchEndpointData<ListAgentTokensResponse>('/api/v1/user/agent-tokens'),
  );
  return { tokens: data?.tokens ?? [], mutate, isLoading, error };
}

export async function createAgentToken(body: { label?: string; teamId?: string; ttlSeconds?: number }) {
  return fetchEndpointData<CreateAgentTokenResponse>('/api/v1/user/agent-tokens', {
    method: 'POST',
    body,
  });
}

export async function revokeAgentToken(id: string) {
  return fetchEndpointData<RevokeAgentTokenResponse>(`/api/v1/user/agent-tokens/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
